// Pure helpers over the lender records: what the website / app receive, what the single switch does, what is
// still missing before a draft lender may be activated. No React here, so the tests run on them directly.
import type { Lender, LenderStatus, LoanType, PartnerType, Product, RequiredCheck, Surface } from '@/types'

export const SURFACE_LABEL: Record<Surface, string> = { website: 'Website', app: 'App' }

export const STATUS_LABEL: Record<LenderStatus, string> = { draft: 'Draft', active: 'On', off: 'Off' }

/** One lender exactly as GET /public/lenders hands it to the website (active lenders only). */
export function publicLender(l: Lender) {
  return {
    slug: l.slug,
    status: l.status,
    name: l.name,
    legal_name: l.legalName,
    rbi_cor: l.rbiCor,
    logo: { light: `/app/lenders/${l.slug}/logo`, dark: `/app/lenders/${l.slug}/logo?theme=dark` },
    brand: { color: l.brand.color },
    short_line: l.shortLine,
    products: l.products.map((p) => ({
      slug: p.slug,
      name: p.name,
      active: p.active,
      endpoint_id: p.endpointState === 'live' ? p.endpointId : null,
      key_facts: {
        amount: { min: p.keyFacts.amountMin, max: p.keyFacts.amountMax },
        tenure_months: { min: p.keyFacts.tenureMinMonths, max: p.keyFacts.tenureMaxMonths },
        rate_pa: { min: p.keyFacts.rateMinPa, max: p.keyFacts.rateMaxPa },
        processing_fee: p.keyFacts.processingFee,
        apr: { min: p.keyFacts.aprMin, max: p.keyFacts.aprMax },
        age: { min: p.keyFacts.ageMin, max: p.keyFacts.ageMax },
        min_income_month: p.keyFacts.minIncomeMonth,
        documents: p.keyFacts.documents,
        disbursal: p.keyFacts.disbursal,
        extra: p.keyFacts.extra,
      },
      kfs: `/app/lenders/${l.slug}/kfs?product=${p.slug.split('.').pop()}`,
    })),
    links: l.links,
    grievance: l.grievance,
    updated_at: l.updatedAt,
  }
}

/** The catalog entry the web app receives (mode + rules per product). */
export function catalogLender(l: Lender) {
  return {
    slug: l.slug,
    name: l.name,
    status: l.status,
    logo: `/app/lenders/${l.slug}/logo`,
    products: l.products
      .filter((p) => p.active)
      .map((p) => ({
        slug: p.slug,
        name: p.name,
        endpoint_id: p.endpointState === 'live' ? p.endpointId : null,
        rules: {
          age: { min: p.keyFacts.ageMin, max: p.keyFacts.ageMax },
          min_income_month: p.keyFacts.minIncomeMonth,
          amount: { min: p.keyFacts.amountMin, max: p.keyFacts.amountMax },
        },
        kfs: `/app/lenders/${l.slug}/kfs?product=${p.slug.split('.').pop()}`,
        button: p.endpointState === 'live' ? 'Apply with ' + l.name : 'Visit ' + l.name,
      })),
  }
}

export interface ImpactLine {
  surface: string
  text: string
}

/** What “Deactivate everywhere” (or “Activate”) does, surface by surface, for the confirm dialog. */
export function switchImpact(l: Lender, turningOn: boolean): ImpactLine[] {
  const lines: ImpactLine[] = []
  const eps = l.workflows.filter((w) => w.state === 'live' || w.state === 'suspended').map((w) => w.endpointId)
  const uniqueEps = [...new Set(eps)]
  if (turningOn) {
    if (l.usedBy.includes('website')) lines.push({ surface: 'Website', text: 'Nothing yet: the website does not read the Hub (its lender cards come from the CMS)' })
    if (l.usedBy.includes('app')) lines.push({ surface: 'Web app', text: 'Back in the app’s catalog at its next fetch (within 5 minutes), if it was published to the app' })
    if (uniqueEps.length) lines.push({ surface: 'Endpoints', text: `${uniqueEps.join(', ')} resumed on their live versions` })
    lines.push({ surface: 'Code, workflows', text: 'Unchanged' })
  } else {
    if (l.usedBy.includes('website')) lines.push({ surface: 'Website', text: 'Nothing yet: the website does not read the Hub (its lender cards come from the CMS)' })
    if (l.usedBy.includes('app')) lines.push({ surface: 'Web app', text: 'Out of the app’s catalog at its next fetch (within 5 minutes): no new offers' })
    if (uniqueEps.length) lines.push({ surface: 'Endpoints', text: `${uniqueEps.join(', ')} suspended; a running workflow finishes` })
    lines.push({ surface: 'Code, workflows', text: 'Kept; the health check keeps running' })
  }
  return lines
}

/** The type is a label only (owner, 2026-10-19): it asks nothing more. */
export const CONNECTION_LABEL: Record<Lender['connection'], string> = { link: 'Link only: the customer goes to the UTM link', api: 'API: we send the application', manual: 'Manual: no link or API; our team enters each lead on their partner portal' }

export const LOAN_TYPE_LABEL: Record<LoanType, string> = { personal: 'Personal loan', business: 'Business loan', gold: 'Gold loan', paylater: 'Buy now, pay later', other: 'Other' }

export const PARTNER_TYPE_LABEL: Record<PartnerType, string> = { lsp: 'LSP / DLA platform', nbfc: 'NBFC', bank: 'Bank', aggregator: 'Aggregator' }

/**
 * The line under the lender's name on the card: who lends when that is filled in (later, on the lender's page), else
 * just the type.
 */
export function lenderLine(l: Pick<Lender, 'partnerType' | 'rbiCor' | 'lenders'>): string {
  const first = l.lenders[0]
  if (first?.legalName.trim()) return `Loans by ${first.legalName.trim()} (${first.kind === 'bank' ? 'Bank' : 'NBFC'})${l.lenders.length > 1 ? ' and partner banks / NBFCs' : ''}`
  if (l.partnerType === 'nbfc' && l.rbiCor.trim()) return `NBFC registered with RBI · CoR ${l.rbiCor.trim()}`
  return PARTNER_TYPE_LABEL[l.partnerType]
}

/** Draft, Live · Link only, Live · API or Off (PF-1): API = connected by API with a live endpoint. */
export function statusLabel(l: Pick<Lender, 'status' | 'connection' | 'workflows'>): string {
  if (l.status === 'draft') return 'Draft'
  if (l.status === 'off') return 'Off'
  if (l.connection === 'manual') return 'Live · Manual'
  return l.connection === 'api' && l.workflows.some((w) => w.state === 'live') ? 'Live · API' : 'Live · Link only'
}

const isLink = (s: string) => /^https:\/\/\S+\.\S+/.test(s.trim())

/**
 * What a lender needs to go live (owner, 2026-10-19: "name logo … utm link"): the name, the logo and the UTM link.
 * Everything else (key facts, features, store links, legal name, CoR, grievance, privacy, API) is optional and can be
 * filled in later on the lender's page. The same rule is api2's requiredChecks. A manual partner (LD-1, WeRize: our
 * team enters its leads on its own portal) has no link, so it needs only the name and the logo.
 */
export function requiredChecks(l: Lender): RequiredCheck[] {
  return [
    { key: 'name', label: 'Lender name', ok: l.name.trim().length >= 2, step: 1 },
    { key: 'logo', label: 'Logo', ok: l.hasLogo, step: 1 },
    ...(l.connection === 'manual' ? [] : [{ key: 'web', label: 'UTM link (https)', ok: isLink(l.links.web), step: 1 as const }]),
  ]
}

export function canActivate(l: Lender): boolean {
  return requiredChecks(l).every((c) => c.ok)
}

export const money = (n: number) => '₹' + n.toLocaleString('en-IN')

export function factsSummary(p: Product): string {
  const f = p.keyFacts
  return `${money(f.amountMin)} – ${money(f.amountMax)} · ${f.tenureMinMonths} – ${f.tenureMaxMonths} m · ${f.rateMinPa}% – ${f.rateMaxPa}% p.a.`
}

export function slugify(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}
